import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Reusable "Add" button component with configurable text, visual variant, size, icon and accessibility attributes.
 * @component
 * @selector app-add-button
 * @example
 *   <app-add-button [buttonText]="'Create'" [variant]="'primary'" [size]="'large'" (buttonClick)="onCreate()"></app-add-button>
 * @input {string} buttonText - The text label shown on the button (default: 'Add').
 * @input {'default'|'primary'|'secondary'|'success'|'danger'} variant - Visual variant that controls styling/theme.
 * @input {'small'|'medium'|'large'} size - Size of the button; affects spacing and font sizing.
 * @input {boolean} disabled - When true, the button is non-interactive and click events are ignored.
 * @input {boolean} fullWidth - When true, the button stretches to fill the width of its container.
 * @input {string=} ariaLabel - Optional aria-label for accessibility when the visible text is not sufficient.
 * @input {string=} customClass - Optional custom CSS class(es) appended to the computed button class list.
 * @input {string=} customIcon - Optional custom icon name or path to display alongside the label.
 * @input {'default'|'break-settings'|'reward-settings'} iconType - Predefined icon variants used by the component.
 * @output {EventEmitter<void>} buttonClick - Emitted when the button is clicked (only emitted if not disabled).
 *
 * Key properties:
 * - buttonClass: string - Computed CSS class string derived from variant, size, fullWidth and customClass.
 *
 * Key methods:
 * - updateButtonClass(): void - Private helper that builds and updates the buttonClass based on inputs (called on init and on changes).
 * - onClick(): void - Public click handler that emits the buttonClick output when the button is not disabled.
 *
 * Lifecycle hooks:
 * - ngOnInit(): void - Initializes the component and computes initial button classes.
 * - ngOnChanges(): void - Recomputes button classes whenever input properties change.
 */
@Component({
  selector: 'app-add-button',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './add-button.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-button.component.scss'],
})
export class AddButtonComponent implements OnInit, OnChanges {
  @Input() buttonText = 'Add';
  @Input() variant: 'default' | 'primary' | 'secondary' | 'success' | 'danger' = 'default';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() disabled = false;
  @Input() fullWidth = false;
  @Input() ariaLabel?: string;
  @Input() customClass?: string;
  @Input() customIcon?: string;
  @Input() iconType?: 'default' | 'break-settings' | 'reward-settings';

  @Output() buttonClick = new EventEmitter<void>();

  buttonClass = '';

  ngOnInit(): void {
    this.updateButtonClass();
  }

  ngOnChanges(): void {
    this.updateButtonClass();
  }

  /**
   * Update the computed CSS class string for the add-button component based on variant, size, fullWidth, and customClass properties.
   * @example
   * updateButtonClass()
   * undefined
   * @param {void} none - No parameters; the method reads component properties instead.
   * @returns {void} No return value; updates this.buttonClass with the computed class string.
   **/
  private updateButtonClass(): void {
    const classes = ['add-button'];

    if (this.variant !== 'default') {
      classes.push(this.variant);
    }

    if (this.size !== 'medium') {
      classes.push(this.size);
    }

    if (this.fullWidth) {
      classes.push('full-width');
    }

    if (this.customClass) {
      classes.push(this.customClass);
    }

    this.buttonClass = classes.join(' ');
  }

  onClick(): void {
    if (!this.disabled) {
      this.buttonClick.emit();
    }
  }
}
